import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MathUtils, Vector3 } from 'three';

import { pairIterator } from '../../../../../utils/generators';
import { isValidNumber } from '../../../../../utils/math/number';
import { CoordinateManager } from '../../../../../viewer/coordinate-manager';
import {
  ProfileSectionGraphMode,
  ProfileSectionTool,
} from '../../../../../viewer/tools/profile-section-tool';
import { getPolyLineLength } from '../../../../../viewer/utilities/math-helpers';
import { useDialog } from '../../../../hooks/use-dialog';
import { useProgressBar } from '../../../../hooks/use-progress-bar';
import { useOptionalUserState } from '../../../../state/user-state/user-state';
import { Button } from '../../../button/button';
import { CheckBox } from '../../../check-box/check-box';
import { Dialog } from '../../../dialog/dialog';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Stack } from '../../../stack/stack';
import { TextBox } from '../../../text-box/text-box';
import {
  exportProfileLinePdfReport,
  exportProfileLinePdfReportImg,
} from '../report-tool-menu/cad-drawing-pdf-report/cad-drawing-pdf';
import { buildGraphSvg } from './convert-svg-to-react-pdf';
import { profileSectionGraphUtils } from './profile-section-graph';
import { getIntersectedEdges, GraphLine } from './profile-section-tool-menu';

export const ExportProfileSectionReportDialog = (props: {
  graphLines: GraphLine[];
  profileLine: Vector3[];
  tool: ProfileSectionTool;
  mode: ProfileSectionGraphMode;
  originalSvg: React.MutableRefObject<SVGSVGElement | null>;
  dialog: ReturnType<typeof useDialog>;
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { t } = useTranslation();
  const userState = useOptionalUserState();
  const authorName = userState ? userState.getName() : '';
  const [loading, setLoading] = useState(false);
  const [centerView, setCenterView] = useState(false);
  const [crossSectionsPerColumn, setCrossSectionsPerColumn] = useState(1);
  const [description, setDescription] = useState('');
  const [crossSectionsPerColumnLabel, setCrossSectionsPerColumnLabel] = useState(
    crossSectionsPerColumn.toString(),
  );
  const [includeAllCrossSections, setIncludeAllCrossSections] = useState(false);
  const { onProgress, renderProgressBar } = useProgressBar({ hideWhenIdle: true });

  const onExportReport = useCallback(async () => {
    const svgRefToExport = centerView ? svgRef.current : props.originalSvg.current;
    if (!svgRefToExport) {
      return;
    }

    setLoading(true);

    try {
      const title =
        props.mode === 'length-profile'
          ? t('profileSection.export.title', { ns: 'skyview' })
          : t('profileSection.export.crossSectionTitle', { ns: 'skyview' });

      const profileLineLength = getPolyLineLength(
        props.tool
          .getProfileLine('length-profile')
          .map((x) => CoordinateManager.getInstance().projectCoordsToLocal(x)),
      );
      const lengthLabel = `${profileLineLength.toFixed(2)} (${props.tool.flattenProfileLine ? '2D' : '3D'})`;

      const options = {
        legendTitle: t('profileSection.export.layerTypes', { ns: 'skyview' }),
        legendItems: props.graphLines,
        footer: {
          title,
          author: authorName,
          profileLineName:
            props.tool.profileLineName ??
            t('section.position.freeHand.text', { ns: 'skyviewDesign' }),
          totalLength: lengthLabel,
          description,
        },
      };

      if (includeAllCrossSections) {
        if (!svgRef.current || !props.tool.lastInterval) {
          return;
        }

        const assets = props.tool.getAssets();
        const mas = CoordinateManager.getInstance().getMAS();
        const xOffset = props.mode === 'cross-section' ? props.tool.crossSectionLeftWidth : 0;
        const reactPdfSvgs: JSX.Element[] = [];

        let offset = 0;
        let i = 0;

        const nextFrame = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
        for (const crossSection of pairIterator(props.tool.crossSectionCoordinates)) {
          const crossSectionGraphLines: GraphLine[] = [];

          for (const data of assets) {
            const result = getIntersectedEdges(crossSection, data, mas, xOffset);
            crossSectionGraphLines.push({
              color: data.color,
              id: data.id,
              name: data.asset.name,
              segments: result,
            });
          }

          profileSectionGraphUtils.createGraph(
            svgRef.current,
            crossSection.map((x) => CoordinateManager.getInstance().projectCoordsToLocal(x)),
            crossSectionGraphLines,
            xOffset,
            offset,
            true,
          );

          offset += props.tool.lastInterval;

          onProgress({
            total: props.tool.crossSectionCoordinates.length / 2,
            current: ++i,
          });

          await nextFrame();

          reactPdfSvgs.push(buildGraphSvg(svgRef.current));
        }

        await exportProfileLinePdfReport(reactPdfSvgs, options, crossSectionsPerColumn);

        return;
      }

      if (centerView) {
        await exportProfileLinePdfReport([buildGraphSvg(svgRefToExport)], options, 1);
      } else {
        await exportProfileLinePdfReportImg(svgRefToExport, options);
      }
    } finally {
      setLoading(false);
    }
  }, [
    centerView,
    props.originalSvg,
    props.mode,
    props.tool,
    props.graphLines,
    t,
    authorName,
    description,
    includeAllCrossSections,
    crossSectionsPerColumn,
    onProgress,
  ]);

  useEffect(() => {
    if (!svgRef.current || !centerView) {
      return;
    }

    const xOffset = props.mode === 'cross-section' ? props.tool.crossSectionLeftWidth : 0;
    profileSectionGraphUtils.createGraph(
      svgRef.current,
      props.profileLine,
      props.graphLines,
      xOffset,
      props.tool.distanceFromStart,
      true,
    );
  }, [props.profileLine, props.graphLines, props.tool, props.mode, centerView]);

  return (
    <Dialog maxHeight="expand" onClose={props.dialog.hide}>
      {{
        header: t('profileSection.export.header', { ns: 'skyview' }),
        content: (
          <Stack spacing={1}>
            {props.mode === 'cross-section' && (
              <CheckBox
                checked={includeAllCrossSections}
                onChange={() =>
                  setIncludeAllCrossSections((oldValue) => {
                    if (!oldValue) {
                      setCenterView(true);
                    }

                    return !oldValue;
                  })
                }
              >
                {t('profileSection.export.includeAllCrossSections', { ns: 'skyview' })} (
                {props.tool.crossSectionCoordinates.length / 2})
              </CheckBox>
            )}
            {includeAllCrossSections && (
              <LabelledContainer
                text={t('profileSection.export.crossSectionsPerColumn', { ns: 'skyview' })}
              >
                <TextBox
                  max={10}
                  min={1}
                  step={1}
                  type="number"
                  value={crossSectionsPerColumnLabel}
                  onChange={(e) => {
                    setCrossSectionsPerColumnLabel(e.target.value);
                    if (isValidNumber(e.target.value)) {
                      setCrossSectionsPerColumn(
                        MathUtils.clamp(Math.floor(Number(e.target.value)), 0, 10),
                      );
                    }
                  }}
                />
              </LabelledContainer>
            )}
            <LabelledContainer
              text={t('cadDrawingReport.pdf.rightPanel.description', { ns: 'skyviewReport' })}
            >
              <TextBox value={description} onChange={(e) => setDescription(e.target.value)} />
            </LabelledContainer>
            <CheckBox
              checked={centerView}
              disabled={includeAllCrossSections}
              onChange={() => setCenterView((oldValue) => !oldValue)}
            >
              {t('focus.tooltip.title', { ns: 'skyviewToolbar' })}
            </CheckBox>
            {centerView && (
              <LabelledContainer text={t('preview', { ns: 'common' })}>
                <div>
                  <svg ref={svgRef} style={{ width: '350px', height: '350px' }} />
                </div>
              </LabelledContainer>
            )}
          </Stack>
        ),
        footer: {
          right: (
            <>
              {renderProgressBar()}
              <Button
                color="primary"
                disabled={loading}
                loading={loading}
                variant="contained"
                onClick={onExportReport}
              >
                {t('confirm', { ns: 'common' })}
              </Button>

              <Button variant="text" onClick={props.dialog.hide}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};
